import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../api/client.ts";
import type { Brand, CampaignRow, Sender } from "../api/types.ts";
import {
  type Audience,
  AudiencePicker,
  EMPTY_AUDIENCE,
  hasRecipients,
  SavedAudience,
} from "../components/AudiencePicker.tsx";
import { confirmAction, toast } from "../components/feedback.tsx";
import { RetireActions, ShowArchived } from "../components/Retire.tsx";
import { TestSendButton } from "../components/TestSendButton.tsx";
import { TimezoneSelect } from "../components/timezones.tsx";
import {
  BrandBadge,
  Card,
  Drawer,
  Empty,
  StatusPill,
  TableSkeleton,
  useDebounced,
} from "../components/ui.tsx";
import { useUnsavedChanges } from "../components/unsaved.ts";

interface TemplateRow {
  id: string;
  brandId: string;
  name: string;
}
interface Progress {
  queued: number;
  submitted: number;
  sent: number;
  failed: number;
  bounced: number;
  /** Deliberately not sent: opted out, left the audience, or Smart Sending. */
  skipped?: number;
}

export function CampaignsPage() {
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [query, setQuery] = useState("");
  const q = useDebounced(query.trim(), 300);
  const navigate = useNavigate();
  const campaigns = useQuery({
    queryKey: ["campaign-rows", showArchived, q],
    queryFn: () => {
      // Broadcasts only: sequences have their own page, as the subtitle says.
      const search = new URLSearchParams({ limit: "100", type: "broadcast" });
      if (showArchived) search.set("archived", "true");
      if (q) search.set("q", q);
      return api.get<CampaignRow[]>(`/analytics/campaigns?${search}`);
    },
    placeholderData: (previous) => previous,
    refetchInterval: (q) => (q.state.data?.some((c) => c.status === "sending") ? 5000 : false),
  });

  const refresh = () => {
    setError(null);
    void qc.invalidateQueries({ queryKey: ["campaign-rows"] });
  };

  const archive = useMutation({
    mutationFn: (id: string) => api.post(`/campaigns/${id}/archive`),
    onSuccess: () => {
      refresh();
      toast("Campaign archived");
    },
    onError: (err: Error) => setError(err.message),
  });
  const unarchive = useMutation({
    mutationFn: (id: string) => api.post(`/campaigns/${id}/unarchive`),
    onSuccess: () => {
      refresh();
      toast("Campaign restored");
    },
    onError: (err: Error) => setError(err.message),
  });
  // Refused by the API once anything has been sent; the message says why.
  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/campaigns/${id}`),
    onSuccess: () => {
      refresh();
      toast("Campaign deleted");
    },
    onError: (err: Error) => setError(err.message),
  });
  const pending = archive.isPending || unarchive.isPending || remove.isPending;

  return (
    <div className="page">
      <div className="page-header">
        <h1>Campaigns</h1>
        <p>One message to a list. Sequences live under their own tab.</p>
      </div>

      <div className="toolbar">
        <input
          type="search"
          placeholder="Search campaigns…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <ShowArchived value={showArchived} onChange={setShowArchived} />
        <div className="spacer" />
        <button type="button" className="btn" onClick={() => setCreating(true)}>
          + New broadcast
        </button>
      </div>

      {error && <div className="error">{error}</div>}

      {creating && (
        <Drawer
          title="New broadcast"
          subtitle="One message to a list. It's saved as a draft; you send or schedule it from its page."
          onClose={() => setCreating(false)}
        >
          <NewBroadcast
            onDone={() => {
              setCreating(false);
              void qc.invalidateQueries({ queryKey: ["campaign-rows"] });
            }}
          />
        </Drawer>
      )}

      <Card>
        {campaigns.isLoading ? (
          <TableSkeleton />
        ) : campaigns.data?.length === 0 ? (
          <Empty>
            {q ? (
              "No campaigns match."
            ) : (
              <>
                No broadcasts yet.{" "}
                <button type="button" className="btn small" onClick={() => setCreating(true)}>
                  + New broadcast
                </button>
              </>
            )}
          </Empty>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Campaign</th>
                <th className="num">Sent</th>
                <th className="num">Replied</th>
                <th className="num">Opened</th>
                <th className="num">Clicked</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {campaigns.data?.map((c) => (
                <tr
                  key={c.id}
                  className="clickable-row"
                  // The whole row opens the report; buttons and links in it
                  // keep their own clicks.
                  onClick={(e) => {
                    if (!(e.target as Element).closest("a, button")) navigate(`/campaigns/${c.id}`);
                  }}
                >
                  <td>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <BrandBadge badge={c.brand_badge} color={c.brand_color} />
                      <div>
                        <Link to={`/campaigns/${c.id}`} style={{ fontWeight: 600 }}>
                          {c.name}
                        </Link>
                        <div style={{ color: "var(--gray-500)", fontSize: 11 }}>
                          {c.type} · {c.brand_name}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="num">{c.sent.toLocaleString()}</td>
                  <td className="num">
                    <Rate n={c.replied} of={c.sent} strong />
                  </td>
                  <td className="num">
                    <Rate n={c.opened} of={c.sent} />
                  </td>
                  <td className="num">
                    <Rate n={c.clicked} of={c.sent} />
                  </td>
                  <td>
                    <StatusPill status={c.status} />
                    {c.status === "scheduled" && c.scheduled_at && (
                      <div style={{ color: "var(--gray-500)", fontSize: 11, marginTop: 2 }}>
                        {formatWhen(c.scheduled_at)}
                      </div>
                    )}
                  </td>
                  <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                    <Link className="btn ghost small" to={`/campaigns/${c.id}`}>
                      {c.type === "broadcast" && c.status === "draft" ? "Manage" : "Report"}
                    </Link>{" "}
                    <RetireActions
                      archived={c.archived_at !== null}
                      pending={pending}
                      onArchive={async () => {
                        if (
                          await confirmAction({
                            title: `Archive ${c.name}?`,
                            message:
                              "It comes off this list and any contacts part way through it stop receiving. Reports are kept.",
                            confirmLabel: "Archive campaign",
                            destructive: true,
                          })
                        )
                          archive.mutate(c.id);
                      }}
                      onUnarchive={() => unarchive.mutate(c.id)}
                      onDelete={
                        c.sent === 0
                          ? async () => {
                              if (
                                await confirmAction({
                                  title: `Delete ${c.name}?`,
                                  message: "This can't be undone.",
                                  confirmLabel: "Delete campaign",
                                  destructive: true,
                                })
                              )
                                remove.mutate(c.id);
                            }
                          : undefined
                      }
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}

/** Local wall-clock now, in the shape a datetime-local input wants. */
function localNow(): string {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
}

/** Always shown in the reader's own timezone, which is the one they typed in. */
function formatWhen(value: string): string {
  return new Date(value).toLocaleString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Sending controls and delivery progress, shown at the top of a broadcast's report. */
export function BroadcastDetail({ id }: { id: string }) {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const detail = useQuery({
    queryKey: ["broadcast", id],
    queryFn: () =>
      api.get<{
        name: string;
        brandId: string;
        status: string;
        scheduledAt: string | null;
        templateId: string | null;
        senderId: string;
        localDelivery: { time: string; fallbackTimezone: string } | null;
        progress: Progress;
        brandName: string | null;
        listName: string | null;
        senderEmail: string | null;
        subject: string | null;
      }>(`/broadcasts/${id}`),
    refetchInterval: (q) => (q.state.data?.status === "sending" ? 4000 : false),
  });
  const recipients = useQuery({
    queryKey: ["broadcast-recipients", id],
    queryFn: () => api.get<{ members: number; eligible: number }>(`/broadcasts/${id}/recipients`),
  });

  /**
   * A datetime-local value, which is wall-clock time with no zone. Sent as an
   * ISO string so the server stores the instant the operator meant rather than
   * the characters they typed.
   */
  const [when, setWhen] = useState("");

  const act = useMutation({
    mutationFn: (action: "schedule" | "send-now" | "pause" | "resume" | "unschedule") =>
      api.post(
        `/broadcasts/${id}/${action}`,
        action === "schedule"
          ? { ...(when ? { scheduledAt: new Date(when).toISOString() } : {}) }
          : undefined,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["broadcast", id] });
      void qc.invalidateQueries({ queryKey: ["campaign-rows"] });
      void qc.invalidateQueries({ queryKey: ["campaign-report", id] });
    },
    onError: (err: Error) => setError(err.message),
  });

  const p = detail.data?.progress;
  const status = detail.data?.status;

  /**
   * Sending a broadcast cannot be undone, and it used to take one click. The
   * confirmation says exactly what is about to happen — how many people,
   * which list and brand, from which address, saying what — so a misclick or
   * the wrong campaign is caught before anyone is mailed.
   */
  const sendNow = async () => {
    const d = detail.data;
    const count = recipients.data?.eligible;
    const ok = await confirmAction({
      title: `Send "${d?.name ?? "this campaign"}" now?`,
      message: [
        count === undefined
          ? "Everyone eligible on the list will be mailed."
          : `${count.toLocaleString()} ${count === 1 ? "person" : "people"} will be mailed${
              d?.listName ? ` from "${d.listName}"` : ""
            }${d?.brandName ? ` (${d.brandName})` : ""}.`,
        d?.senderEmail ? `From: ${d.senderEmail}.` : "",
        d?.subject ? `Subject: "${d.subject}".` : "",
        "This can't be undone; you can pause it once it starts.",
      ]
        .filter(Boolean)
        .join(" "),
      confirmLabel: count === undefined ? "Send now" : `Send to ${count.toLocaleString()}`,
      destructive: true,
    });
    if (ok) act.mutate("send-now");
  };

  return (
    <Card
      title="Sending"
      action={
        detail.data?.templateId ? (
          <TestSendButton templateId={detail.data.templateId} senderId={detail.data.senderId} />
        ) : undefined
      }
    >
      <div className="card-body">
        {error && <div className="error">{error}</div>}

        {detail.data && (
          <SavedAudience
            campaignId={id}
            brandId={detail.data.brandId}
            editable={status === "draft" || status === "scheduled"}
          />
        )}

        {(status === "draft" || status === "scheduled") && detail.data && (
          <LocalDelivery campaignId={id} saved={detail.data.localDelivery} />
        )}

        {p && (
          <div className="kpi-grid" style={{ marginBottom: 12 }}>
            <div className="kpi">
              <div className="label-mono">Queued</div>
              <div className="kpi-value">{p.queued}</div>
            </div>
            <div className="kpi">
              <div className="label-mono">Submitted</div>
              <div className="kpi-value">{p.submitted}</div>
            </div>
            <div className="kpi">
              <div className="label-mono">Delivered</div>
              <div className="kpi-value">{p.sent}</div>
            </div>
            <div className="kpi">
              <div className="label-mono">Failed</div>
              <div className="kpi-value">{p.failed + p.bounced}</div>
            </div>
            {(p.skipped ?? 0) > 0 && (
              <div
                className="kpi"
                title="Deliberately not sent: unsubscribed, left the audience, or mailed recently (Smart Sending). The report says which."
              >
                <div className="label-mono">Skipped</div>
                <div className="kpi-value">{p.skipped}</div>
              </div>
            )}
          </div>
        )}

        {status === "scheduled" && detail.data?.scheduledAt && (
          <div className="notice" style={{ marginBottom: 12 }}>
            Scheduled for <strong>{formatWhen(detail.data.scheduledAt)}</strong>. The worker picks
            it up on the first tick after that.
          </div>
        )}

        {status === "draft" && (
          <div className="field" style={{ marginBottom: 12 }}>
            <label htmlFor="b-when">Send at</label>
            <input
              id="b-when"
              type="datetime-local"
              value={when}
              min={localNow()}
              onChange={(e) => setWhen(e.target.value)}
            />
            <div className="hint">
              Your local time. Leave it empty and Schedule means the next tick, which is the same as
              sending now.
            </div>
          </div>
        )}

        <div className="toolbar" style={{ marginBottom: 0 }}>
          {status === "draft" && (
            <>
              <button type="button" className="btn" onClick={sendNow}>
                Send now
              </button>
              <button
                type="button"
                className="btn ghost"
                disabled={act.isPending}
                onClick={() => act.mutate("schedule")}
              >
                {when ? `Schedule for ${formatWhen(when)}` : "Schedule"}
              </button>
            </>
          )}
          {status === "scheduled" && (
            <>
              <button type="button" className="btn" onClick={sendNow}>
                Send now instead
              </button>
              <button
                type="button"
                className="btn ghost"
                disabled={act.isPending}
                onClick={() => act.mutate("unschedule")}
              >
                Unschedule
              </button>
            </>
          )}
          {status === "sending" && (
            <button type="button" className="btn ghost" onClick={() => act.mutate("pause")}>
              Pause
            </button>
          )}
          {status === "paused" && (
            <button type="button" className="btn" onClick={() => act.mutate("resume")}>
              Resume
            </button>
          )}
          {status === "sent" && <span className="pill ok">Finished</span>}
        </div>

        <p className="caveat" style={{ marginTop: 12, marginBottom: 0 }}>
          A broadcast sends at the time you choose — there is no business-hours window. It still
          respects each sender&rsquo;s daily cap and warmup ramp.
        </p>
      </div>
    </Card>
  );
}

function NewBroadcast({ onDone }: { onDone: () => void }) {
  const [brandId, setBrandId] = useState("");
  const [senderId, setSenderId] = useState("");
  const [audience, setAudience] = useState<Audience>(EMPTY_AUDIENCE);
  const [templateId, setTemplateId] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);

  const brands = useQuery({
    queryKey: ["brands"],
    queryFn: () => api.get<Brand[]>("/config/brands"),
  });
  const senders = useQuery({
    queryKey: ["senders"],
    queryFn: () => api.get<Sender[]>("/config/senders"),
  });
  const domains = useQuery({
    queryKey: ["domains"],
    queryFn: () => api.get<{ id: string; brandId: string }[]>("/config/domains"),
  });
  const templates = useQuery({
    queryKey: ["templates"],
    queryFn: () => api.get<TemplateRow[]>("/templates"),
  });

  useEffect(() => {
    if (!brandId && brands.data?.[0]) setBrandId(brands.data[0].id);
  }, [brandId, brands.data]);

  /**
   * A sender on another brand's domain fails DKIM alignment, and a template
   * written for one brand carries the wrong footer for another. The API rejects
   * both; filtering here means nobody gets that far.
   */
  const brandDomainIds = new Set(
    (domains.data ?? []).filter((d) => d.brandId === brandId).map((d) => d.id),
  );
  const brandSenders = (senders.data ?? []).filter((s) => brandDomainIds.has(s.domainId));
  const brandTemplates = (templates.data ?? []).filter((t) => t.brandId === brandId);

  const create = useMutation({
    mutationFn: () =>
      api.post("/broadcasts", {
        brandId,
        senderId,
        templateId,
        name,
        createdBy: "web",
        ...audience,
      }),
    onSuccess: () => {
      onDone();
      toast("Campaign created");
    },
    onError: (err: Error) => setError(err.message),
  });

  useUnsavedChanges(Boolean(name.trim()));

  return (
    <Card>
      <form
        className="card-body"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          setError(null);
          create.mutate();
        }}
      >
        {error && <div className="error">{error}</div>}

        <div className="row">
          <div className="field">
            <label htmlFor="b-name">Name</label>
            <input id="b-name" value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
          <div className="field">
            <label htmlFor="b-brand">Brand</label>
            <select
              id="b-brand"
              value={brandId}
              onChange={(e) => {
                setBrandId(e.target.value);
                setSenderId("");
                setAudience(EMPTY_AUDIENCE);
                setTemplateId("");
              }}
            >
              {brands.data?.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="row">
          <div className="field">
            <label htmlFor="b-sender">From</label>
            <select
              id="b-sender"
              value={senderId}
              onChange={(e) => setSenderId(e.target.value)}
              required
            >
              <option value="">Choose a sender…</option>
              {brandSenders.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.displayName} — {s.email}
                </option>
              ))}
            </select>
            {brandSenders.length === 0 && (
              <div className="hint">
                No senders on this brand's domains. Add one under{" "}
                <Link to="/settings/domains">Domains &amp; senders</Link>.
              </div>
            )}
          </div>
          <div className="field">
            <label htmlFor="b-template">Template</label>
            <select
              id="b-template"
              value={templateId}
              onChange={(e) => setTemplateId(e.target.value)}
              required
            >
              <option value="">Choose a template…</option>
              {brandTemplates.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
            {brandTemplates.length === 0 && (
              <div className="hint">
                No templates for this brand. Create one under <Link to="/templates">Templates</Link>
                .
              </div>
            )}
          </div>
        </div>

        <AudiencePicker brandId={brandId} value={audience} onChange={setAudience} />

        <button
          className="btn"
          type="submit"
          disabled={create.isPending || !hasRecipients(audience)}
          title={
            hasRecipients(audience)
              ? undefined
              : "Pick at least one list, segment or disposition to send to"
          }
        >
          Create draft
        </button>
      </form>
    </Card>
  );
}

/**
 * A count with its rate of what was sent, the rate leading: "12%" says more
 * at a glance than "48" without its denominator. Replies are bold — the
 * signal this app trusts, where opens are inflated by privacy proxies.
 */
function Rate({ n, of, strong }: { n: number; of: number; strong?: boolean }) {
  if (of === 0) return <span style={{ color: "var(--gray-400)" }}>—</span>;
  const pct = Math.round((n / of) * 1000) / 10;
  return (
    <span title={`${n.toLocaleString()} of ${of.toLocaleString()}`}>
      <span style={strong ? { fontWeight: 700 } : undefined}>{pct}%</span>
      <span className="rate-n">{n.toLocaleString()}</span>
    </span>
  );
}

/**
 * Deliver at a local hour in each recipient's own zone, rather than to
 * everyone the moment it goes out: 9am in Chicago and 9am in Los Angeles,
 * two hours apart.
 */
function LocalDelivery({
  campaignId,
  saved,
}: {
  campaignId: string;
  saved: { time: string; fallbackTimezone: string } | null;
}) {
  const qc = useQueryClient();
  const [time, setTime] = useState(saved?.time ?? "09:00");
  const [fallback, setFallback] = useState(saved?.fallbackTimezone ?? "America/New_York");
  const set = useMutation({
    mutationFn: (on: boolean) =>
      api.put(`/broadcasts/${campaignId}/local-delivery`, {
        time: on ? time : null,
        fallbackTimezone: fallback,
      }),
    onSuccess: (_, on) => {
      toast(
        on
          ? `Delivers at ${time} in each recipient's time zone`
          : "Delivers as soon as it goes out",
      );
      void qc.invalidateQueries({ queryKey: ["broadcast", campaignId] });
    },
    onError: (err: Error) => toast(err.message, "bad"),
  });
  const on = Boolean(saved);
  const changed = on && (saved?.time !== time || saved?.fallbackTimezone !== fallback);

  return (
    <div className="local-delivery">
      <label className="check">
        <input
          type="checkbox"
          checked={on}
          disabled={set.isPending}
          onChange={(e) => set.mutate(e.target.checked)}
        />{" "}
        Deliver at a set time in each recipient&rsquo;s time zone
      </label>
      {on && (
        <div className="local-delivery-fields">
          <input
            type="time"
            aria-label="Local delivery time"
            value={time}
            onChange={(e) => setTime(e.target.value)}
          />
          <TimezoneSelect value={fallback} onChange={setFallback} />
          {changed && (
            <button type="button" className="btn small" onClick={() => set.mutate(true)}>
              Save
            </button>
          )}
        </div>
      )}
      <div className="hint">
        {on
          ? `Each person gets it at ${saved?.time} their time, today if that's still ahead for them, else tomorrow. Their zone is the one set on the contact, else their US or Canadian phone's area code, else the one picked here.`
          : "Off: everyone is mailed as soon as it goes out, within the sender's limits."}
      </div>
    </div>
  );
}
